import type { ReactNode } from 'react';
import { Box, TableCell, TableRow, Typography } from '@mui/material';

import { railsLine } from '../copy';
import type { FoldedPayer, PayerAudience, RailView } from '../types';

import { StateChip } from './StateChip';
import { StatusAgeLine } from './StatusAgeLine';
import { StediStatusList } from './StediStatusList';

export interface PayerRowProps {
  payer: FoldedPayer;
  audience?: PayerAudience;
  /**
   * The action cell's content. AC-7 (the "Open in Stedi" / "Submit first
   * claim" links) is GDN-3406's to wire — this component only renders
   * whatever it's given, or nothing. It sits under "what's happening", as
   * the design places it.
   */
  action?: ReactNode;
  /** Reference instant for the "Waiting" column; omit to leave the column empty. */
  asOf?: Date;
}

const cellSx = {
  verticalAlign: 'top',
  borderColor: '#eeeef4',
  paddingTop: '15px',
  paddingBottom: '15px',
} as const;

/**
 * One row of the clinic Payer Connections table (AC-6), in the Payer Handoff
 * layout: the payer and its rails, who has it, what's happening plus the
 * row's action, and how long it has been waiting. Presentational only —
 * expects a `<TableBody>` ancestor.
 */
export function PayerRow({ payer, action, audience, asOf }: PayerRowProps) {
  const rails = Object.values(payer.rails).filter((rail): rail is RailView => Boolean(rail));
  return (
    <TableRow>
      <TableCell sx={{ ...cellSx, minWidth: 0 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1c1c22' }}>
          {payer.payerName}
        </Typography>
        {rails.length > 0 && (
          <Typography sx={{ fontSize: '12px', color: '#8a8a96', mt: '3px' }}>
            {railsLine(rails, audience)}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={cellSx}>
        <StateChip state={payer.state} audience={audience} />
      </TableCell>
      <TableCell sx={{ ...cellSx, minWidth: 0 }}>
        <Typography sx={{ fontSize: '13px', lineHeight: 1.55, color: '#33333d' }}>
          {payer.whatsHappening}
        </Typography>
        {action && <Box sx={{ mt: '6px' }}>{action}</Box>}
      </TableCell>
      {audience === 'staff' && (
        <TableCell sx={{ ...cellSx, minWidth: 220 }}>
          <StediStatusList
            statuses={payer.stediStatuses}
            ariaLabel={`Stedi statuses for ${payer.payerName}`}
          />
        </TableCell>
      )}
      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
        {asOf && (
          <StatusAgeLine
            statusUpdatedAt={payer.selectedRail?.statusUpdatedAt}
            asOf={asOf}
            state={payer.state}
          />
        )}
      </TableCell>
    </TableRow>
  );
}
